import React, { useRef } from 'react';
import { Grid, TextField } from '@mui/material';
import PropTypes from 'prop-types';

const DateInput = ({
  nameDay,
  nameMonth,
  nameYear,
  values,
  handleChange,
  handleBlur,
  errors,
  touched,
}) => {
  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  const handleDayChange = (e) => {
    if (parseInt(e.target.value, 10) < 0) {
      e.target.value = '';
    }
    handleChange(e);
    if (e.target.value.length === 2 && monthRef.current) {
      monthRef.current.focus();
    }
  };

  const handleMonthChange = (e) => {
    if (parseInt(e.target.value, 10) < 0) {
      e.target.value = '';
    }
    handleChange(e);
    if (e.target.value.length === 2 && yearRef.current) {
      yearRef.current.focus();
    }
  };

  const handleYearChange = (e) => {
    if (parseInt(e.target.value, 10) < 0) {
      e.target.value = '';
    }
    handleChange(e);
    if (e.target.value.length === 4) {
      e.target.blur();
    }
  };

  const handleDayBlur = (e) => {
    let value = e.target.value;
    if (value) {
      const num = parseInt(value, 10);
      if (num < 1 || num > 31) {
        value = '';
      } else if (value.length === 1) {
        value = '0' + value;
      }
    }
    if (value !== e.target.value) {
      handleChange({ ...e, target: { ...e.target, value } });
    }
    handleBlur(e);
  };

  const handleMonthBlur = (e) => {
    let value = e.target.value;
    if (value) {
      const num = parseInt(value, 10);
      if (num < 1 || num > 12) {
        value = '';
      } else if (value.length === 1) {
        value = '0' + value;
      }
    }
    if (value !== e.target.value) {
      handleChange({ ...e, target: { ...e.target, value } });
    }
    handleBlur(e);
  };

  const handleYearBlur = (e) => {
    const currentYear = new Date().getFullYear();
    let value = e.target.value;
    if (value !== '' && value !== null && typeof value !== 'undefined') {
      const num = parseInt(value, 10);
      if (Number.isNaN(num)) {
        value = '';
      } else {
        const clamped = Math.max(1900, Math.min(currentYear, num));
        value = String(clamped);
      }
    }
    if (value !== e.target.value) {
      handleChange({ ...e, target: { ...e.target, value } });
    }
    handleBlur(e);
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={4}>
        <TextField
          label="DD"
          name={nameDay}
          type="number"
          inputRef={dayRef}
          value={values[nameDay]}
          onChange={handleDayChange}
          onBlur={handleDayBlur}
          fullWidth
          size="small"
          inputProps={{ min: 1, max: 31 }}
          required
          error={touched[nameDay] && Boolean(errors[nameDay])}
          helperText={touched[nameDay] && errors[nameDay]}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          label="MM"
          name={nameMonth}
          type="number"
          inputRef={monthRef}
          value={values[nameMonth]}
          onChange={handleMonthChange}
          onBlur={handleMonthBlur}
          fullWidth
          size="small"
          inputProps={{ min: 1, max: 12 }}
          required
          error={touched[nameMonth] && Boolean(errors[nameMonth])}
          helperText={touched[nameMonth] && errors[nameMonth]}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          label="AAAA"
          name={nameYear}
          type="number"
          inputRef={yearRef}
          value={values[nameYear]}
          onChange={handleYearChange}
          onBlur={handleYearBlur}
          fullWidth
          size="small"
          inputProps={{ min: 1900, max: new Date().getFullYear() }}
          required
          error={(() => {
            const cy = new Date().getFullYear();
            const val = values[nameYear];
            const num = Number(val);
            const within = Number.isFinite(num) && num >= 1900 && num <= cy;
            return touched[nameYear] && Boolean(errors[nameYear]) && !within;
          })()}
          helperText={(() => {
            const cy = new Date().getFullYear();
            const val = values[nameYear];
            const num = Number(val);
            const within = Number.isFinite(num) && num >= 1900 && num <= cy;
            return touched[nameYear] && Boolean(errors[nameYear]) && !within ? errors[nameYear] : '';
          })()}
        />
      </Grid>
    </Grid>
  );
};

DateInput.propTypes = {
  nameDay: PropTypes.string.isRequired,
  nameMonth: PropTypes.string.isRequired,
  nameYear: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  errors: PropTypes.object,
  touched: PropTypes.object,
};

export default DateInput;
